/**
 * Motore budget.
 *
 * Il budget INFLUENZA le proposte ma non le censura: nulla viene nascosto perche'
 * costoso. Ogni proposta porta il proprio semaforo e, se sfora, il sistema spiega
 * l'impatto e offre alternative.
 */

import type { BudgetLevel, BudgetCategory, PriceTier } from '../domain.js';
import { MEAL_COST_ESTIMATE_MINOR } from '../domain.js';

/** Oltre questa quota del limite si passa da "ok" ad "attenzione". */
const WARN_THRESHOLD = 0.85;

/**
 * Classifica una spesa prevista rispetto al limite del giorno e a quello totale.
 *
 *   ok      dentro il previsto
 *   warn    oltre l'85% del limite giornaliero
 *   over    supera il giorno ma non il totale del viaggio
 *   blocked supera anche il totale
 *
 * Senza limiti impostati non esiste sforamento: il livello e' sempre `ok`.
 */
export function classifyBudget(params: {
  plannedMinor: number;
  dayLimitMinor: number | null;
  totalPlannedMinor?: number;
  totalLimitMinor?: number | null;
}): BudgetLevel {
  const { plannedMinor, dayLimitMinor } = params;
  const totalLimit = params.totalLimitMinor ?? null;
  const totalPlanned = params.totalPlannedMinor ?? plannedMinor;

  if (totalLimit !== null && totalPlanned > totalLimit) return 'blocked';
  if (dayLimitMinor === null) return 'ok';
  if (plannedMinor > dayLimitMinor) return 'over';
  if (plannedMinor > dayLimitMinor * WARN_THRESHOLD) return 'warn';
  return 'ok';
}

export interface BudgetImpact {
  readonly level: BudgetLevel;
  readonly beforeMinor: number;
  readonly afterMinor: number;
  readonly limitMinor: number | null;
  readonly overshootMinor: number;
  /** Testo pronto per l'interfaccia, in tono umano. */
  readonly message: string;
}

/**
 * Impatto dell'aggiunta di una tappa sul budget della giornata.
 * Serve a rispondere prima del click: "se lo aggiungi, cosa succede?".
 */
export function projectImpact(params: {
  currentPlannedMinor: number;
  addedCostMinor: number | null;
  dayLimitMinor: number | null;
  totalPlannedMinor?: number;
  totalLimitMinor?: number | null;
  formatAmount: (minor: number) => string;
}): BudgetImpact {
  const added = params.addedCostMinor ?? 0;
  const before = params.currentPlannedMinor;
  const after = before + added;
  const limit = params.dayLimitMinor;

  const level = classifyBudget({
    plannedMinor: after,
    dayLimitMinor: limit,
    ...(params.totalPlannedMinor !== undefined
      ? { totalPlannedMinor: params.totalPlannedMinor + added }
      : {}),
    ...(params.totalLimitMinor !== undefined ? { totalLimitMinor: params.totalLimitMinor } : {}),
  });

  const overshoot = limit === null ? 0 : Math.max(0, after - limit);
  const format = params.formatAmount;

  let message: string;
  if (params.addedCostMinor === null) {
    message = 'Costo non disponibile: verificalo prima di andarci.';
  } else if (limit === null) {
    message = `Costo previsto ${format(added)}.`;
  } else {
    switch (level) {
      case 'ok':
        message = `Ci stai dentro: ${format(after)} su ${format(limit)}.`;
        break;
      case 'warn':
        message = `Sei vicino al limite: ${format(after)} su ${format(limit)}.`;
        break;
      case 'over':
        message = `Il budget di oggi passa da ${format(before)} a ${format(after)} su ${format(limit)}: sforeresti di ${format(overshoot)}.`;
        break;
      case 'blocked':
        message = `Questa spesa supera il budget totale del viaggio.`;
        break;
    }
  }

  return { level, beforeMinor: before, afterMinor: after, limitMinor: limit, overshootMinor: overshoot, message };
}

/**
 * Stima del costo di un pasto per fascia di prezzo.
 * Sempre presentata come stima: non conosciamo i menu e non li inventiamo.
 */
export function estimateMealCostMinor(tier: PriceTier | null): number | null {
  if (tier === null) return null;
  const range = MEAL_COST_ESTIMATE_MINOR[tier];
  return Math.round((range.min + range.max) / 2);
}

/** Fascia di prezzo compatibile con quanto resta del budget del giorno. */
export function affordablePriceTiers(remainingMinor: number | null): PriceTier[] {
  if (remainingMinor === null) return ['€', '€€', '€€€'];
  return (['€', '€€', '€€€'] as const).filter(
    (tier) => MEAL_COST_ESTIMATE_MINOR[tier].min <= remainingMinor,
  );
}

export interface AlternativeCandidate {
  readonly id: string;
  readonly name: string;
  readonly costMinor: number | null;
  readonly distanceM: number;
  readonly categoryLabel: string;
  readonly score: number;
}

/**
 * Alternative quando una scelta sfora.
 *
 * Non si propone semplicemente "la cosa piu' economica": si cerca il miglior
 * rapporto fra qualita' (score) e risparmio, entro una distanza ragionevole.
 * L'obiettivo resta massimizzare la qualita' del viaggio, non minimizzare la spesa.
 */
export function pickAlternatives(
  candidates: readonly AlternativeCandidate[],
  params: { maxCostMinor: number; maxDistanceM: number; limit?: number },
): AlternativeCandidate[] {
  const affordable = candidates.filter(
    (candidate) =>
      (candidate.costMinor ?? 0) <= params.maxCostMinor &&
      candidate.distanceM <= params.maxDistanceM,
  );

  return [...affordable]
    .sort((a, b) => {
      // Prima la qualita', poi la vicinanza. Il costo e' gia' un filtro.
      if (b.score !== a.score) return b.score - a.score;
      return a.distanceM - b.distanceM;
    })
    .slice(0, params.limit ?? 3);
}

export interface CategoryTotals {
  readonly category: BudgetCategory;
  readonly plannedMinor: number;
  readonly actualMinor: number;
}

export function sumCategories(entries: readonly CategoryTotals[]): {
  planned: number;
  actual: number;
} {
  return entries.reduce(
    (acc, entry) => ({
      planned: acc.planned + entry.plannedMinor,
      actual: acc.actual + entry.actualMinor,
    }),
    { planned: 0, actual: 0 },
  );
}

/** Microcopy del riepilogo giornaliero. Tono umano, non bancario. */
export function describeDayBudget(params: {
  spentMinor: number;
  limitMinor: number | null;
  formatAmount: (minor: number) => string;
}): string {
  if (params.limitMinor === null) return 'Nessun limite impostato per oggi.';
  const remaining = params.limitMinor - params.spentMinor;
  if (remaining < 0) {
    return `Hai superato il budget di oggi di ${params.formatAmount(Math.abs(remaining))}.`;
  }
  if (remaining === 0) return 'Hai usato esattamente il budget di oggi.';
  return `Ti restano ${params.formatAmount(remaining)} per oggi.`;
}
